import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GLOW_PALETTE } from '../data/homeBooks';
import { requireAuth, isLoggedIn } from '../utils/auth';
import { useDominantColor } from '../utils/dominantColor';
import { useFavorites } from '../utils/favorites';
import { usePlaylist } from '../utils/playlist';
import { useRatingMap } from '../utils/stats';
import PremiumLink from './PremiumLink';
import BookDetailsModal from './BookDetailsModal';

const HeadphonesIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 14v-1a9 9 0 0 1 18 0v1" />
    <path d="M21 14v3a2 2 0 0 1-2 2h-1v-6h1a2 2 0 0 1 2 1z" fill="currentColor" stroke="none" />
    <path d="M3 14v3a2 2 0 0 0 2 2h1v-6H5a2 2 0 0 0-2 1z" fill="currentColor" stroke="none" />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9.5" />
    <line x1="12" y1="11" x2="12" y2="16.5" />
    <circle cx="12" cy="7.5" r="0.4" fill="currentColor" />
  </svg>
);

const WaveformIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3"  y1="12" x2="3"  y2="12" className="wave-bar wave-1" />
    <line x1="7"  y1="9"  x2="7"  y2="15" className="wave-bar wave-2" />
    <line x1="11" y1="6"  x2="11" y2="18" className="wave-bar wave-3" />
    <line x1="15" y1="9"  x2="15" y2="15" className="wave-bar wave-4" />
    <line x1="19" y1="11" x2="19" y2="13" className="wave-bar wave-5" />
  </svg>
);

export default function BookCard({ book, index = 0, lang = 'en' }) {
  const navigate = useNavigate();
  const isRtl = lang === 'ar';
  const cover = (isRtl && book.coverImageAr) ? book.coverImageAr : book.coverImage;
  const displayTitle = (isRtl && book.titleAr) ? book.titleAr : book.title;
  const dominantRgb = useDominantColor(cover);
  const fallbackGlow = book.glow || GLOW_PALETTE[index % GLOW_PALETTE.length];
  const style = dominantRgb ? { '--glow': dominantRgb } : undefined;
  const readPath = `/read/${book._id}?lang=${lang}`;
  const quizPath = `/quiz/${book._id}?lang=${lang}`;
  const favorites = useFavorites();
  const playlist = usePlaylist();
  // Average + count from the shared cache. Hidden if the admin
  // toggled `ratingsVisible` off on this book, or if there are no
  // votes yet. (Module-level cache means 50 cards = 1 fetch.)
  const ratingMap = useRatingMap();
  const ratingInfo = ratingMap.get(String(book._id));
  const showRating = book.ratingsVisible !== false && ratingInfo && ratingInfo.count > 0;
  const isFav = favorites.has(book._id);
  const inPlaylist = playlist.has(book._id);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const openDetails = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDetailsOpen(true);
  };
  const closeDetails = () => setDetailsOpen(false);

  // Auth-only gate kept for the (unlikely) case where a non-logged-in user
  // somehow lands on a card; subscription gating happens inside PremiumLink.
  const requireLoggedIn = (path) => (e) => {
    if (!isLoggedIn()) {
      e.preventDefault();
      requireAuth(navigate, path);
    }
  };

  const onToggleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn()) {
      requireAuth(navigate, '/');
      return;
    }
    try {
      await favorites.toggle(book._id);
    } catch {
      /* swallow — UI already reverted by helper */
    }
  };

  const onTogglePlaylist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn()) {
      requireAuth(navigate, '/');
      return;
    }
    try {
      await playlist.toggle(book._id);
    } catch {
      /* swallow — UI already reverted by helper */
    }
  };

  return (
    <div
      className={`profile-card ${dominantRgb ? '' : `glow-${fallbackGlow}`}`}
      style={style}
    >
      <div role="button" tabIndex={0} aria-label={`About ${displayTitle}`} onClick={openDetails} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) openDetails(e); }} className="profile-card-thumb has-cover">
        <img src={cover} alt={displayTitle} />
        <button
          type="button"
          className={`profile-playlist-btn is-button ${inPlaylist ? 'is-active' : ''}`}
          aria-label={inPlaylist ? 'Remove from playlist' : 'Add to playlist'}
          aria-pressed={inPlaylist}
          title={inPlaylist ? 'In your playlist' : 'Add to playlist'}
          onClick={onTogglePlaylist}
        >
          {inPlaylist ? <WaveformIcon /> : <HeadphonesIcon />}
        </button>
        <button
          type="button"
          className={`profile-fav-star is-button ${isFav ? 'is-filled' : 'is-outline'}`}
          aria-label={isFav ? 'Remove from favourites' : 'Add to favourites'}
          aria-pressed={isFav}
          onClick={onToggleFav}
        >
          {isFav ? '★' : '☆'}
        </button>
        <button
          type="button"
          className="profile-card-details-btn"
          aria-label="Show details"
          title="Show details"
          onClick={openDetails}
        >
          <InfoIcon />
          <span>Details</span>
        </button>
      </div>
      <div className="profile-card-body" dir={isRtl ? 'rtl' : 'ltr'}>
        <div className="profile-card-title">{displayTitle}</div>
        <div className="profile-card-meta">
          {book.author}
          {book.ageRange && <span className="home-age-badge"> · {isRtl ? 'الأعمار' : 'Ages'} {book.ageRange}</span>}
          {showRating && (
            <span className="home-rating-badge" title={`${ratingInfo.average} out of 5 from ${ratingInfo.count} ${ratingInfo.count === 1 ? 'rating' : 'ratings'}`}>
              <span aria-hidden="true"> · ★ </span>
              {ratingInfo.average.toFixed(1)}
              <span className="home-rating-count" aria-hidden="true"> ({ratingInfo.count})</span>
            </span>
          )}
        </div>
        <div className="profile-card-actions">
          <PremiumLink to={readPath} className="card-action-btn card-action-read">Read</PremiumLink>
          <PremiumLink to={quizPath} onClick={requireLoggedIn(quizPath)} className="card-action-btn card-action-quiz">Quiz</PremiumLink>
        </div>
      </div>

      <BookDetailsModal
        open={detailsOpen}
        onClose={closeDetails}
        book={book}
        lang={lang}
        readPath={readPath}
        quizPath={quizPath}
        onActionClick={requireLoggedIn}
      />
    </div>
  );
}
